import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of NotificationsSlice — plan §5.2. 18 of web's 31 and mobile's 31 reducers are
 * identical and live here; the state below is the 7 fields the two apps agree on.
 *
 * ℹ️ Mobile's file is 609 lines but only ~180 of them are live — lines 16-417 are a
 * commented-out `billData` fixture. It is NOT carried across; a comment is not behaviour, and
 * `viewOrderBillSuccess`'s trailing `// state.billData = billData;` (the line that used it) is
 * dropped for the same reason.
 *
 * Authored per platform instead:
 * (`clearAllNewOnlineOrders` USED to be here — mobile's copy carried a stray
 * `console.log('=clearAllNewOnlineOrders slice==')` and was otherwise identical. Under the
 * standing rule as REVISED 2026-08-19, dead code that is the only thing making the platforms
 * differ is removed rather than preserved, so the log is gone and the reducer moved into
 * `sharedReducers` below.)
 * - `markAsReadNotificationSuccess`, `markAllAsReadSuccess`, `rejectApproveOrderSuccess` —
 *   identical logic, but web writes `NOTIFICATION_VARIABLES.read` / `.rejected` where mobile
 *   writes the string literals `'Read'` / `'Rejected'`. The VALUES match exactly, so this is
 *   runtime-identical — but mobile's own `NOTIFICATION_VARIABLES` has only five keys and
 *   defines no `rejected`/`approved`, so unifying on the constant would mean adding keys to
 *   mobile's constant surface to serve a cosmetic merge. Left divergent (§5.2: `sharedReducers`
 *   holds byte-identical bodies only).
 * - `acceptApproveOrderSuccess` — the same constant-vs-literal split, PLUS mobile's
 *   `console.log('messageId', messageId)`. ⚠️ The log is dead debug output, but removing it
 *   does NOT collapse this reducer — the constant-vs-literal difference survives, and mobile's
 *   `NOTIFICATION_VARIABLES` has no `approved` key to unify on. So rule 2(a) does not fire and
 *   the log stays (flagged in the ledger as a cleanup candidate instead).
 * - `getAllNewOnlineOrders` and `testNotification` — identical (empty) bodies, opposite ARITY.
 *   Type-level, not runtime (ledger hazard 14), and load-bearing in BOTH directions:
 *     web  `getAllNewOnlineOrders(state, action)` -> dispatched with a payload at 5 call sites
 *                                   (`{autoRefresh, skipGlobalLoader}` from the notifications
 *                                   middleware, `{}` from Header/Login/MyOrdersHeaderMb/
 *                                   saleHelpers); mobile calls `getAllNewOnlineOrders()` bare.
 *     mob  `testNotification(state, action)`      -> `dispatch(testNotification(timestamp))`,
 *                                   and its saga reads `action.payload` to build
 *                                   `ENDPOINT.NOTIFICATIONS.testNotification(action.payload)`
 *                                   — the `?_dc={id}` form of hazard 4's divergent key. Web
 *                                   dispatches `testNotification()` bare against `/notification/test`.
 *   Unifying either would break a build or advertise an affordance the other saga ignores.
 * - web's 6 extra reducers (`clearAllNotifications`, `getAllNewOnlinePollingOrdersSuccess`,
 *   `updateOtherNotification`, `showStopNotificationNudge`, `notificationCurrentStatus`,
 *   `postActivityLog`) and its `showStopNotificationNudge` / `isNotificationStop` /
 *   `allNewOnlinePollingOrders` state.
 * - mobile's 6 (`storeNewOnlineOrders`, `notificationTray1Data`, `clearAllOtherNotifications`,
 *   `clearNotificationTray`, `shouldRenderOrder`, `saveNotificationPermission`) and its
 *   `notificationListData` / `shouldRenderOrder` / `haveNotificationPermission` state.
 * - web's `userLogout`/`supportLogout` `extraReducers`, which are EMPTY on purpose — see
 *   index.desktop.ts. Mobile registers none at all.
 *
 * `removeAcceptedOrder` IS shared: the only difference the analyser flags is a trailing comma
 * after the `.filter()` callback in mobile's copy. Pure formatting, verified by eye.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. `any` makes RTK
 * infer the NO-PAYLOAD overload, so every `dispatch(x(payload))` stops compiling. Reducers
 * the apps wrote with no `action` param at all keep that shape.
 */
export interface SharedNotificationsState {
  newOnlineOrders: any;
  allNewOnlineOrders: any;
  showOnlineNotification: any;
  currOtherNotifications: any;
  allOtherNotifications: any;
  extranotifications: any;
  billData: any;
}

export const sharedInitialState: SharedNotificationsState = {
  newOnlineOrders: [],
  showOnlineNotification: {},
  allNewOnlineOrders: [],
  currOtherNotifications: [],
  allOtherNotifications: {},
  extranotifications: [],
  billData: {},
};

export const sharedReducers = {
  getAllOtherNotifications: (state: SharedNotificationsState, action: PayloadAction<any>) => {},

  getOtherNotifications: (state: SharedNotificationsState, action: PayloadAction<any>) => {
    if (!!state.allOtherNotifications.results?.length) {
      const updatedOtherNotifications = action.payload;
      updatedOtherNotifications.results = [
        ...state.allOtherNotifications.results,
        ...updatedOtherNotifications.results,
      ];
      state.allOtherNotifications = updatedOtherNotifications;
    } else {
      state.allOtherNotifications = action.payload;
    }
  },
  removeAcceptedOrder: (state: SharedNotificationsState, action: PayloadAction<any>) => {
    state.allNewOnlineOrders = state.allNewOnlineOrders.filter(
      (order: any) => {
        if (order._id === action.payload.orderId) return false;
        return true;
      }
    );
  },
  removeOrderFromNotifications: (state: SharedNotificationsState, action: PayloadAction<any>) => {
    state.newOnlineOrders = action.payload?.orders;
  },
  toggleOnlineNotificationsModal: (state: SharedNotificationsState, action: PayloadAction<any>) => {
    state.showOnlineNotification = action.payload;
  },
  storeCurrOtherNotifications: (state: SharedNotificationsState, action: PayloadAction<any>) => {
    if (!!state.currOtherNotifications?.length) {
      state.currOtherNotifications = [
        action.payload,
        ...state.currOtherNotifications,
      ];
    } else {
      state.currOtherNotifications = [action.payload];
    }
  },
  closeNotifications: (state: SharedNotificationsState) => {
    state.newOnlineOrders = [];
    state.currOtherNotifications = [];
    state.showOnlineNotification = { show: false };
  },
  // Shared as of 2026-08-19. The two apps' bodies were identical apart from a stray
  // `console.log('=clearAllNewOnlineOrders slice==')` in mobile's copy — debug output with no
  // functional effect (and stripped from web's prod build anyway). Removed under standing
  // rule 2(a): dead code that is the ONLY thing making the platforms differ.
  clearAllNewOnlineOrders: (state: SharedNotificationsState) => {
    state.allNewOnlineOrders = [];
  },
  markAsReadNotification: (state: SharedNotificationsState, action: PayloadAction<any>) => {},
  markAllAsRead: () => {},
  acceptApproveOrder: (state: SharedNotificationsState, action: PayloadAction<any>) => {},
  rejectApproveOrder: (state: SharedNotificationsState, action: PayloadAction<any>) => {},
  viewOrderBill: (state: SharedNotificationsState, action: PayloadAction<any>) => {},
  viewOrderBillSuccess: (state: SharedNotificationsState, action: PayloadAction<any>) => {
    if (action.payload?.saleItems) {
      state.billData = action.payload;
    } else state.billData = {};
  },
  viewPurchaseOrder: (state: SharedNotificationsState, action: PayloadAction<any>) => {},
  viewPurchaseOrderSuccess: (state: SharedNotificationsState, action: PayloadAction<any>) => {},
  viewFeedback: (state: SharedNotificationsState, action: PayloadAction<any>) => {},
  viewFeedbackSuccess: (state: SharedNotificationsState, action: PayloadAction<any>) => {},
  getAllNewOnlineOrdersSuccess: (state: SharedNotificationsState, action: PayloadAction<any>) => {
    state.allNewOnlineOrders = action.payload;
  },
};
